import { t } from "components/CentralDataMangement/translation/Translation";
import Title from "components/common/atoms/Title";
import { useEffect, useState } from "react";
import {
  Candidate,
  OfficeTarget,
  PersonalTarget,
  WeeklyReportsData,
} from "../Annotations";
import WeeklyReportTable from "../common/WeeklyReportTable";
import Tick from "static/images/Tick";
import OfficeTargetTable from "../common/OfficeTargetTable";
import PersonalTargetTable from "../common/PersonalTargetTable";
import { ApiCall } from "components/common/services/ApiServices";
import { EDIT_WEEKLY_REPORTS } from "../../../../routes/ApiEndpoints";
import { CENTRAL_DATA_MANAGEMENT_MICROSERVICE } from "Constants";
import EncryptDecryptService from "components/common/services/EncryptDecryptService";
import CustomNotify from "components/common/atoms/CustomNotify";
import { Link, useSearchParams } from "react-router-dom";
import LoadingIcon from "components/common/utlis/LoadingIcon";
import AccessControl from "components/common/services/RolesAndPermissions/AccessControl";

interface TargetData {
  officeTarget: OfficeTarget;
  personalTarget: PersonalTarget;
  from?: string;
  to?: string;
  officeName?: string;
}

const WeeklyReports: React.FC = () => {
  const [reports, setReports] = useState<WeeklyReportsData>({});
  // Per-locale activity wording from the API, so rows follow the application language
  // instead of showing the raw follow-up keys.
  const [activityLabels, setActivityLabels] = useState<Record<string, string>>({});
  const [candidates, setCandidates] = useState<Candidate[]>([]);
  const [targetData, setTargetData] = useState<TargetData>();
  const [loading, setLoading] = useState(true);
  // let data = secureLocalStorage.getItem('weeklyReport') as string; // Get the 'data' query param as a string
  // const { data } = useParams<{ data: string }>();
  const [searchParams] = useSearchParams();
  const data = searchParams.get("data");

  useEffect(() => {
    const timeout = setTimeout(() => {
      fetchData();
    }, 3000); // 3 seconds

    return () => clearTimeout(timeout); // Cleanup the timeout on component unmount
  }, []);

  const fetchData = async () => {
    let parsedData = {};
    try {
      if (data) {
        // Decrypt the recruiterData
        let decryptedData = decodeURIComponent(EncryptDecryptService.decryptData(data));

        // If decryptedData is in JSON format, parse it
        parsedData = JSON.parse(decryptedData);
      }

    } catch (error) {
      CustomNotify({
        type: "error",
        message: t("Something went wrong"),
      });
      return null;
    }
    const response = await ApiCall.service(
      EDIT_WEEKLY_REPORTS,
      "POST",
      { ...parsedData, type: "recruiter" },
      false,
      CENTRAL_DATA_MANAGEMENT_MICROSERVICE
    );

    if (response?.status === 200) {
      setReports(response?.data?.reports);
      setActivityLabels(response?.data?.activityLabels ?? {});
      setCandidates(response?.data?.candidates);
      setTargetData(response?.data?.targets);
    } else {
      CustomNotify({
        type: "error",
        message: t("Some error occured")
      });
    }
    setLoading(false);
  };

  const rows = [
    { key: "calls", label: "Calls" },
    // { key: "screening", label: "Screenings" },
    { key: "registration", label: "Registration" },
    { key: "registration_fro", label: "Registration foreign" },
    { key: "re_registration", label: "Re-registration" },
    { key: "re_registration_fro", label: "Re-registration foreign" },
    { key: "presentation", label: "Presentation" },
    { key: "presentation_fro", label: "Presentation foreign" },
    { key: "interview", label: "Interview" },
    { key: "starter", label: "Starter" },
    { key: "stopper", label: "Stopper" },
    { key: "stopper_permanent", label: "Stopper permanent" },
  ];

  return (
    <AccessControl
      requiredPermissions={[
        {
          permission: "Reports",
          read: true,
        },
      ]}
      renderNoAccess={true}
    >
      <Title title={`${t("Reporting (weekly recruiter)")} - ${targetData?.officeName ?? ""} (${targetData?.from ?? "-"} to ${targetData?.to ?? "-"})`} />
      {loading ? (
        <div style={{ height: "38vw" }} className="d-flex align-items-center justify-content-center">
          <LoadingIcon
            iconType="bars"
            color="#e55496"
            className="m-auto"
            height={"5vw"}
            width={"5vw"}
          />
        </div>

      ) : (
        <>
          <div className="row">
            <div className="col-md-4">
              <OfficeTargetTable
                data={targetData?.officeTarget}
                tableTitle={t("Office")}
              />
            </div>
            <div className="col-md-8">
              <PersonalTargetTable
                data={targetData?.personalTarget}
                tableTitle={targetData?.personalTarget?.name ?? "-"}
              />
            </div>
          </div>
          <div className="row marginTop1">
            <div className="col-md-12">
              <WeeklyReportTable data={reports as WeeklyReportsData} rows={rows} activityLabels={activityLabels} />
            </div>
          </div>
          <div className="marginBotttom1 weekly-report-table scrollBarDesign">
            <table className="table table-hover table-bordered targetTable">
              <thead>
                <tr className="TableHeader">
                  <th className="text-break text-center" style={{ width: "15%" }}>
                    {t("Candidate")}
                  </th>
                  <th className="text-break text-center" style={{ width: "16%" }}>
                    {t("Profile")}
                  </th>
                  <th className="text-break text-center" style={{ width: "7%" }}>
                    {t("Registration")}
                  </th>
                  <th className="text-break text-center" style={{ width: "10%" }}>
                    {t("Registration FRO")}
                  </th>
                  <th className="text-break text-center" style={{ width: "8%" }}>
                    {t("Re-registration")}
                  </th>
                  <th className="text-break text-center" style={{ width: "11%" }}>
                    {t("Re-registration FRO")}
                  </th>
                  <th className="text-break text-center" style={{ width: "7%" }}>
                    {t("Presentation")}
                  </th>
                  <th className="text-break text-center" style={{ width: "10%" }}>
                    {t("Presentation FRO")}
                  </th>
                  <th className="text-break text-center" style={{ width: "6%" }}>
                    {t("Interview")}
                  </th>
                  <th className="text-break text-center" style={{ width: "5%" }}>
                    {t("Starter")}
                  </th>
                  <th className="text-break text-center" style={{ width: "5%" }}>
                    {t("Stopper")}
                  </th>
                </tr>
              </thead>
              <tbody>
                {candidates && candidates.length > 0 ? (
                  <>
                    {candidates.map((candidate: Candidate, index) => (
                      <tr key={index}>
                        <td className="align-middle text-center">{candidate.candidate}</td>
                        <td className="align-middle text-center">{candidate.profile}</td>
                        <td className="table-action-icons align-middle text-center">
                          {candidate.registration > 0 ? <Tick /> : ""}
                        </td>
                        <td className="table-action-icons align-middle text-center">
                          {candidate.registration_fro > 0 ? <Tick /> : ""}
                        </td>
                        <td className="table-action-icons align-middle text-center">
                          {candidate.re_registration > 0 ? <Tick /> : ""}
                        </td>
                        <td className="table-action-icons align-middle text-center">
                          {candidate.re_registration_fro > 0 ? <Tick /> : ""}
                        </td>
                        <td className="text-center">
                          <div className="numberWrapper">
                            {candidate.presentation}
                          </div>
                        </td>
                        <td className="text-center">
                          <div className="numberWrapper">
                            {candidate.presentation_fro}
                          </div>
                        </td>
                        <td className="text-center">
                          <div className="numberWrapper">
                            {candidate.interview}
                          </div>
                        </td>
                        <td className="table-action-icons align-middle text-center">
                          {candidate.starter > 0 ? <Tick /> : ""}
                        </td>
                        <td className="table-action-icons align-middle text-center">
                          {candidate.stopper > 0 ? <Tick /> : ""}
                        </td>
                      </tr>
                    ))}
                  </>
                ) : (
                  <tr className="border rounded-3">
                    <td colSpan={10}>
                      <div className="text-danger text-center">
                        {t("No candidates found")}
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          <div className="row backPadding">
            <div className="col-md-4 align-self-center">
              <Link
                to="/manage-reports"
                className=" back-btn text-decoration-underline"
              >
                {t("Back")}
              </Link>
            </div>
          </div>
        </>
      )}

    </AccessControl>
  );
};

export default WeeklyReports;
